// src/pages/SendMessage.jsx
import React, { useState, useEffect } from 'react';
import { getConnections, sendMessage } from '../api';

function formatIST(dateString) {
  if (!dateString) return 'later';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

export default function SendMessage() {
  const [connections, setConnections] = useState([]);
  const [selectedDeviceToken, setSelectedDeviceToken] = useState('');
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [delaySeconds, setDelaySeconds] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [sendResult, setSendResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchConnections = async () => {
    try {
      const res = await getConnections();
      if (res.status === 1) {
        const connectionsData = res.data || [];
        setConnections(connectionsData);
        const firstConnected = connectionsData.find(c => c.status === 'connected');
        if (firstConnected) {
          setSelectedDeviceToken(firstConnected.device_token);
        } else if (connectionsData.length > 0) {
          setSelectedDeviceToken(connectionsData[0].device_token);
        }
      }
    } catch (e) {
      console.error('Failed to fetch connections', e);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  // CHANGE 1: Default media_type to empty string
  const addMediaField = () => setMediaItems([...mediaItems, { media_url: '', caption: '', media_type: '' }]);
  
  const updateMediaField = (i, field, value) => {
    const updated = [...mediaItems];
    updated[i][field] = value;
    setMediaItems(updated);
  };
  
  const removeMediaField = (i) => setMediaItems(mediaItems.filter((_, idx) => idx !== i));

  const handleSend = async (e) => {
    e.preventDefault();
    setSendResult(null);
    setLoading(true);

    if (!selectedDeviceToken) {
      setSendResult({ ok: false, error: 'Please select a device' });
      setLoading(false);
      return;
    }

    const media = mediaItems
      .filter((m) => m.media_url && m.media_url.trim())
      .map((m) => {
        let fileName = "file";
        try {
          const urlParts = new URL(m.media_url.trim());
          const pathParts = urlParts.pathname.split('/');
          const extractedName = pathParts[pathParts.length - 1];
          if (extractedName && extractedName !== '') {
            fileName = extractedName;
          }
        } catch (e) {
          fileName = "media";
        }

        const mediaObj = {
          url: m.media_url.trim(),
          caption: m.caption?.trim() || '',
          name: fileName
        };

        if (m.media_type && m.media_type.trim() !== '') {
          mediaObj.media_type = m.media_type.trim();
        }

        return mediaObj;
      });

    try {
      const result = await sendMessage(
        selectedDeviceToken,
        to,
        message,
        media,
        delaySeconds || null,
        scheduleAt || null
      );

      if (result.status === 1) {
        const data = result.data || {};
        let successMsg = '';
        if (data.sent) {
          successMsg = `✅ Message sent! (ID: ${data.waMessageId || data.id || 'N/A'})`;
        } else if (data.queued) {
          const scheduledFor = data.scheduledFor ? formatIST(data.scheduledFor) : 'later';
          successMsg = `⏳ Message scheduled for ${scheduledFor}`;
        } else {
          successMsg = result.description || 'Message processed';
        }
        setSendResult({ ok: true, message: successMsg });
        // Clear form
        setTo('');
        setMessage('');
        setMediaItems([]);
        setDelaySeconds('');
        setScheduleAt('');
      } else {
        throw new Error(result.description || 'Failed to send message');
      }
    } catch (err) {
      console.error('Send error:', err);
      setSendResult({ ok: false, error: err.message || 'Failed to send message' });
    } finally {
      setLoading(false);
    }
  }; // <-- CHANGE 2: ADDED THE MISSING CLOSING BRACE HERE

  const connectedConnections = connections.filter(c => c.status === 'connected');

  if (connections.length === 0) {
    return (
      <div className="card">
        <p>No devices found. Please add a device from the Dashboard first.</p>
      </div>
    );
  }

  if (connectedConnections.length === 0) {
    return (
      <div className="card">
        <p>No connected devices. Please connect a WhatsApp device first from the Dashboard.</p>
        {connections.length > 0 && (
          <div>
            <p>Available devices:</p>
            <ul>
              {connections.map(c => (
                <li key={c.id}>{c.name || 'Unnamed'} - Status: {c.status}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <form onSubmit={handleSend}>
        <label>Select Device</label>
        <select
          value={selectedDeviceToken}
          onChange={(e) => setSelectedDeviceToken(e.target.value)}
          required
          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
        >
          {connectedConnections.map(conn => (
            <option key={conn.id} value={conn.device_token}>
              {conn.name || conn.phone_number || 'Unnamed'} ({conn.phone_number || 'No phone'}) - {conn.status}
            </option>
          ))}
        </select>

        <label>Recipient number</label>
        <input type="text" value={to} onChange={(e) => setTo(e.target.value)} placeholder="919876543210" required />

        <label>Message</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} />

        <label>Media (optional)</label>
        {mediaItems.map((item, i) => (
          <div key={i} className="media-row">
            <input
              type="text"
              placeholder="Media URL"
              value={item.media_url}
              onChange={(e) => updateMediaField(i, 'media_url', e.target.value)}
            />
            <input
              type="text"
              placeholder="Caption"
              value={item.caption}
              onChange={(e) => updateMediaField(i, 'caption', e.target.value)}
            />
            <select
              value={item.media_type}
              onChange={(e) => updateMediaField(i, 'media_type', e.target.value)}
              style={{ width: '80px' }}
            >
              {/* CHANGE 3: Added 'None' option */}
              <option value="">None</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
            </select>
            <button type="button" className="btn-icon" onClick={() => removeMediaField(i)}>✕</button>
          </div>
        ))}
        <button type="button" className="btn-secondary" onClick={addMediaField}>+ Add media</button>

        <div className="two-col">
          <div>
            <label>Delay (seconds)</label>
            <input
              type="number"
              value={delaySeconds}
              onChange={(e) => setDelaySeconds(e.target.value)}
              min="0"
              placeholder="e.g. 5"
            />
          </div>
          <div>
            <label>Schedule (IST)</label>
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
            />
            <small className="text-gray-400">Pick a date and time. Leave empty to use delay.</small>
          </div>
        </div>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Sending...' : 'Send'}
        </button>

        {sendResult && (
          <p className={sendResult.ok ? 'result-ok' : 'result-error'}>
            {sendResult.ok ? sendResult.message : `Error: ${sendResult.error}`}
          </p>
        )}
      </form>
    </div>
  );
}